import React, { useContext, useEffect, useState } from "react";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Pagination from '@mui/material/Pagination';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LoadingButton from '@mui/lab/LoadingButton';
import AuthContext from "../../context/auth/authContext";
import DestinationContext from "../../context/destination/destinationContext";
import AdminContext from "../../context/admin/adminContext";
import AlertContext from "../../context/alert/alertContext";
import AddDestinationComposer from "./AddDestinationComposer";

const DestinationsTab = () => {
  const destinationContext = useContext(DestinationContext);
  const { destinations, getDestinations, updateDestination, deleteDestination } = destinationContext;

  const alertContext = useContext(AlertContext);
  const { setAlert } = alertContext;

  const [selected, setSelected] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCoverPhoto, setEditCoverPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    getDestinations();
    // eslint-disable-next-line
  }, []);

  const handleSelect = (dest) => {
    setSelected(dest);
    setEditName(dest.name || '');
    setEditDescription(dest.description || '');
    setEditCoverPhoto(null);
  };

  const handleSave = async () => {
    if (!selected || selected === 'new') return;
    setSaving(true);
    const fields = { name: editName, description: editDescription };
    if (editCoverPhoto) fields.cover_photo = editCoverPhoto;
    const result = await updateDestination(selected.id, fields);
    setSaving(false);
    if (result.ok) {
      setAlert('Destination updated', 'success');
      setSelected(result.data);
    } else {
      setAlert(result.msg || 'Failed to update', 'error');
    }
  };

  const handleDelete = async () => {
    if (!selected || selected === 'new') return;
    setDeleteConfirmOpen(false);
    const result = await deleteDestination(selected.id);
    if (result.ok) {
      setAlert('Destination deleted', 'success');
      setSelected(null);
    } else {
      setAlert(result.msg || 'Failed to delete', 'error');
    }
  };

  const editPanel = selected && selected !== 'new' && (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>Edit Destination</Typography>
      {selected.place_address && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, p: 1.5,
          border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50',
        }}>
          <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{selected.place_name || selected.name}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{selected.place_address}</Typography>
          </Box>
        </Box>
      )}
      <TextField fullWidth label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} variant="outlined" size="small" />
      <TextField fullWidth multiline rows={4} label="Description" value={editDescription} onChange={(e) => setEditDescription(e.target.value)} variant="outlined" size="small" />
      <Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Cover photo</Typography>
        {(editCoverPhoto || selected.cover_photos?.[0]?.image_link) && (
          <Box component="img"
            src={editCoverPhoto ? URL.createObjectURL(editCoverPhoto) : selected.cover_photos[0].image_link}
            sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 2, mb: 1 }} />
        )}
        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />}
          sx={{ textTransform: 'none', borderStyle: 'dashed', color: 'text.secondary', width: '100%' }}>
          {editCoverPhoto ? editCoverPhoto.name : 'Change cover photo'}
          <input type="file" accept="image/*" hidden onChange={(e) => { if (e.target.files[0]) setEditCoverPhoto(e.target.files[0]); }} />
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <LoadingButton variant="contained" onClick={handleSave} loading={saving} endIcon={<SaveIcon />} loadingPosition="end"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}>Save</LoadingButton>
        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirmOpen(true)}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Delete</Button>
      </Box>
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete destination?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete "{selected.name}". Any trips linked to this destination will lose their destination reference.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
      <Box sx={{
        width: { xs: '100%', md: 260 }, flexShrink: 0,
        border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', alignSelf: 'flex-start',
      }}>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50',
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Destinations</Typography>
          <IconButton size="small" onClick={() => setSelected('new')} title="Add destination"><AddIcon fontSize="small" /></IconButton>
        </Box>
        <List disablePadding sx={{ maxHeight: { xs: 200, md: 480 }, overflowY: 'auto' }}>
          {destinations && destinations.map((dest) => (
            <ListItemButton key={dest.id} selected={selected && selected !== 'new' && selected.id === dest.id} onClick={() => handleSelect(dest)} sx={{ py: 1 }}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar variant="rounded" src={dest.cover_photos?.[0]?.image_link} sx={{ width: 32, height: 32 }}>
                  <LocationOnIcon fontSize="small" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary={dest.name} primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                secondary={dest.place_address} secondaryTypographyProps={{ variant: 'caption', noWrap: true }} />
            </ListItemButton>
          ))}
          {(!destinations || destinations.length === 0) && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>No destinations yet</Typography>
            </Box>
          )}
        </List>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 640 }}>
        {selected === 'new' && <AddDestinationComposer />}
        {selected && selected !== 'new' && editPanel}
        {!selected && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>Select a destination to edit, or click + to add a new one.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const UsersTab = () => {
  const adminContext = useContext(AdminContext);
  const { users, usersPage, usersTotal, usersPageSize, getUsers, updateUserRole } = adminContext;

  const alertContext = useContext(AlertContext);
  const { setAlert } = alertContext;

  useEffect(() => {
    getUsers(1);
    // eslint-disable-next-line
  }, []);

  const handlePageChange = (_, page) => {
    getUsers(page);
  };

  const handleRoleChange = async (userId, newRole) => {
    const result = await updateUserRole(userId, newRole);
    if (result.ok) {
      setAlert(`Role updated to ${newRole}`, 'success');
    } else {
      setAlert(result.msg || 'Failed to update role', 'error');
    }
  };

  const totalPages = Math.ceil(usersTotal / usersPageSize);

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users && users.map((u) => (
              <TableRow key={u.user_id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={u.avatar?.image_link} sx={{ width: 32, height: 32 }}>
                      {(u.first_name || u.user_id || '?')[0].toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {u.name || ((u.first_name || '') + ' ' + (u.last_name || '')).trim() || u.user_id}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{u.user_id}</Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    select size="small" variant="outlined"
                    value={u.role || 'user'}
                    onChange={(e) => handleRoleChange(u.user_id, e.target.value)}
                    sx={{ minWidth: 100, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="user">
                      <Chip label="user" size="small" variant="outlined" sx={{ borderRadius: 1 }} />
                    </MenuItem>
                    <MenuItem value="admin">
                      <Chip label="admin" size="small" color="primary" sx={{ borderRadius: 1 }} />
                    </MenuItem>
                  </TextField>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {u.created_at ? new Date(u.created_at * 1000).toLocaleDateString() : '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={usersPage} onChange={handlePageChange} color="primary" />
        </Box>
      )}
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, textAlign: 'center' }}>
        {usersTotal} user{usersTotal !== 1 ? 's' : ''} total
      </Typography>
    </Box>
  );
};

const Manage = () => {
  const authContext = useContext(AuthContext);
  const { loadUser, user, isAuthenticated } = authContext;
  const [tab, setTab] = useState(0);

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line
  }, []);

  if (!isAuthenticated || !user) {
    return (
      <Box sx={{ width: '100%', pt: 12, textAlign: 'center' }}>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>Please log in to continue.</Typography>
      </Box>
    );
  }

  if (user.role !== 'admin') {
    return (
      <Box sx={{ width: '100%', pt: 12, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Access Denied</Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mt: 1 }}>You need admin privileges to access this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pt: 8 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Manage</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Manage destinations, users, and content.
        </Typography>
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Destinations" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab label="Users" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>
      {tab === 0 && <DestinationsTab />}
      {tab === 1 && <UsersTab />}
    </Box>
  );
};

export default Manage;
